<template>
  <div
    v-if="engineState.isRunning === false"
    class="admin-starter section__row"
  >
    <BasicButton
      label="Gérer Mon Espace"
      @click="lancerAdmin"
    />
  </div>
  <div
    v-else
    class="admin-container section__row"
  >
    <BasicText
      size="body-m"
      color="secondary-900"
    >
      Veuillez sélectionner une entité juridique et renseigner un SIRET, SIREN ou une raison sociale, ou bien
      sélectionner un utilisateur et renseigner un email, un nom ou un prénom.
    </BasicText>
    <div class="admin-wrapper">
      <BasicToggleSwitch
        v-if="contextHidden !== true"
        :readonly="contextReadOnly"
        v-model="context"
        :items="contextLibelle"
      />
      <BasicToggleSwitch
        v-if="modeHidden !== true"
        :readonly="modeReadOnly"
        v-model="mode"
        :items="modeLibelle"
      />
      <template v-if="identifiantHidden !== true">
        <BasicAutocomplete
          v-if="mode === 'contact'"
          v-model="contact"
          :search="searchContactInt"
          :search-min-length="3"
          key-id="email"
          key-label="email"
          placeholder="Nom, prénom, email"
          class="autocomplete"
          :readonly="itentifiantReadOnly"
          strict
        >
          <template #default="{ email, username }">
            <div class="flex-1 flex flex-column">
              <template v-if="username && !email?.startsWith(username)">
                <div class="flex-1">
                  <BasicText class="flex-1">{{ username }}</BasicText>
                </div>
                <div class="flex-1">
                  <BasicText
                    size="body-s"
                    class="flex-1"
                  >
                    {{ email }}
                  </BasicText>
                </div>
              </template>
              <div
                v-else
                class="flex-1"
              >
                <BasicText class="flex-1">{{ email }}</BasicText>
              </div>
            </div>
          </template>
        </BasicAutocomplete>
        <BasicAutocomplete
          v-if="mode === 'entite'"
          v-model="entite"
          :search="searchEntiteInt"
          :search-min-length="estModeEntiteSirenSiret ? 9 : 1"
          key-id="identifiant"
          :get-input-text="getTextEntite"
          :placeholder="estModeEntiteSirenSiret ? 'Siren ou Siret' : 'Raison sociale, siren, siret'"
          class="autocomplete"
          :readonly="itentifiantReadOnly"
          strict
          :search-formater="sirenSiretSearchFormater"
        >
          <template #default="{ denomination, siren, nic }">
            <div class="flex-1">
              <div class="flex">
                <BasicText class="flex-1">{{ denomination }}</BasicText>
                <BasicTag
                  v-if="!nic"
                  label="Entreprise"
                  size="medium"
                  class="tag--danger"
                />
              </div>
              <div class="flex flex-gap-1">
                <BasicText
                  v-if="nic"
                  size="body-s"
                >
                  Siret :
                </BasicText>
                <BasicText
                  v-else
                  size="body-s"
                >
                  Siren :
                </BasicText>
                <BasicText
                  size="body-s"
                  weight="bold"
                >
                  {{ siren }} {{ nic }}
                </BasicText>
              </div>
            </div>
          </template>
        </BasicAutocomplete>
      </template>
      <BasicButton
        type="secondary"
        variant="filled"
        :label="mode === 'entite' ? 'Ajouter un utilisateur' : 'Ajouter une entité'"
        :iconName="mode === 'entite' ? 'profile' : 'entreprise'"
        class="flex-none btn-ajout"
        @click="ajouteHabilitation"
        :disabled="!isReady || isReadonly"
      />
    </div>
    <HabilitationsAdminContent
      @autorisations-changed="(reason, autorisation) => $emit('autorisationsChanged', reason, autorisation)"
    />
  </div>
</template>

<script setup lang="ts">
  import { watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import {
    type HabilitationsAdminEmits,
    type HabilitationsAdminMode,
    type HabilitationsAdminProps,
    HabilitationsAdminModeLibelle,
  } from './HabilitationsAdmin.type'
  import { objectToDropdownItems } from '@/features/interface/shared/tools'
  import {
    typeContextCourantToContextAppType,
    TypesContextesAutorisesEnum,
    TypesContextesAutorisesEnumLibelle,
    type ContextAppType,
    type TypeContextCourant,
  } from '@/features/auth/context-app/types'
  import { useHabilitationsAdminEngineStore, type Entite } from './useHabilitationsAdminEngineStore'
  import { isNotNullUndefinedOrEmptyString } from '@/features/shared/tools'
  import type { ContactDTO } from '@/features/api/habilitation/dto/contact'
  import { isSiren, isSiret } from '@/features/shared/tools/luhn'
  import HabilitationsAdminContent from './HabilitationsAdminContent.vue'
  import { useHabilitationAdd } from './useHabilitationAdd'
  import { sirenSiretSearchFormater } from '@/features/interface/shared/tools'

  const props = withDefaults(defineProps<HabilitationsAdminProps>(), {
    autoRun: true,
    context: 'adherent',
    mode: 'entite',
  })

  const contextLibelle = [TypesContextesAutorisesEnum.Adherent, TypesContextesAutorisesEnum.Prestataire].map(
    (context) => ({
      id: typeContextCourantToContextAppType(context as TypeContextCourant),
      label: TypesContextesAutorisesEnumLibelle[context],
    }),
  )
  const modeLibelle = objectToDropdownItems(HabilitationsAdminModeLibelle)

  const emits = defineEmits<HabilitationsAdminEmits>()

  const storeEngine = useHabilitationsAdminEngineStore()

  const {
    engineSetupFieldWithKey,
    engineSetupFieldWithOptions,
    engineSetupCustomUpdater,
    searchContact,
    searchEntite,
    initialise,
  } = storeEngine
  const { dataToken, engineState, estModeEntiteSirenSiret, isReady, isReadonly } = storeToRefs(storeEngine)

  const context = engineSetupFieldWithOptions<ContextAppType | undefined>({
    getValue(model) {
      return model.context
    },
    setPatch(patch, value) {
      patch.context = value
      patch.identifiantModel = undefined
    },
  })
  const mode = engineSetupFieldWithOptions<HabilitationsAdminMode | undefined>({
    getValue(model) {
      return model.mode
    },
    setPatch(patch, value) {
      patch.mode = value
      patch.identifiant = undefined
      patch.identifiantModel = undefined
    },
  })

  const getTextEntite = (value: Entite) => {
    if (value.nic) return value.denomination
    return `${value.denomination} - Entreprise`
  }

  const updateJwtToken = engineSetupCustomUpdater<string>((patch, newValue) => {
    patch.jwtToken = newValue
  })

  /* Patchers */
  const contact = engineSetupFieldWithKey<ContactDTO | undefined>('identifiantModel')
  const entite = engineSetupFieldWithKey<Entite | undefined>('identifiantModel')
  const searchEntiteInt = (search: string, signal: AbortSignal) => {
    const emptyResult = Promise.resolve([])
    search = search.trim()
    if (search.length === 0) return emptyResult
    if (estModeEntiteSirenSiret.value) {
      if (/^(\d{9}|\d{14})$/.test(search) === false) return emptyResult
      if (search.length === 9 && !isSiren(search)) return emptyResult
      if (search.length === 14 && !isSiret(search)) return emptyResult
    }
    return searchEntite(search, signal)
  }
  const searchContactInt = (search: string, signal: AbortSignal) => {
    search = search.trim()
    if (search.length === 0) return Promise.resolve([])
    return searchContact(search, signal)
  }

  const lancerAdmin = () => initialise(props, true)

  const ajouteHabilitation = async () => {
    const popup = useHabilitationAdd()
    try {
      const habilitation = await popup.showAddHabilitations()
      emits('autorisationsChanged', 'Add', habilitation)
    } catch {}
  }

  /* Notifie la modification des props */
  watch(
    () => engineState.value.context,
    (newValue, oldValue) => {
      if (newValue && newValue !== oldValue) emits('contextChanged', newValue)
    },
  )

  watch(
    () => engineState.value.mode,
    (newValue, oldValue) => {
      if (newValue && newValue !== oldValue) emits('modeChanged', newValue)
    },
  )

  watch(
    () => engineState.value.identifiant,
    (newValue, oldValue) => {
      if (newValue !== oldValue) emits('identifiantChanged', newValue)
    },
  )

  watch(
    () => isReady.value,
    () => {
      emits('queryChanged', isReady.value, {
        context: context.value,
        identifiant: engineState.value.identifiant,
        mode: engineState.value.mode,
      })
    },
  )

  /* Notifie les changements */
  /* Todo : Reste autorisations-changed */
  watch(
    () => !!engineState.value.isRunning,
    (newValue, oldValue) => {
      if (newValue !== oldValue) emits('runStarted', newValue)
    },
  )

  watch(
    () => dataToken.value,
    (newValue, oldValue) => {
      if (newValue !== oldValue) emits('tokenValidated', isNotNullUndefinedOrEmptyString(dataToken.value))
    },
  )

  /* Observe la modification des props */
  watch(
    () => props.jwtToken,
    (newToken) => updateJwtToken(newToken),
  )
  watch(
    () => ({ ...props }),
    (newProps, oldProps) => {
      // console.log(JSON.parse(JSON.stringify(newProps)), JSON.parse(JSON.stringify(oldProps ?? {})))
      const propsEquals =
        (newProps.autoRun === oldProps?.autoRun || newProps.autoRun === engineState.value.autoRun) &&
        (newProps.context === oldProps?.context || newProps.context === engineState.value.context) &&
        (newProps.identifiant === oldProps?.identifiant || newProps.identifiant === engineState.value.identifiant) &&
        (newProps.mode === oldProps?.mode || newProps.mode === engineState.value.mode)

      if (propsEquals) return
      // console.log('## Props lancent : initialise #########################')
      initialise(props)
    },
    { deep: true, immediate: true },
  )
</script>

<style scoped lang="less">
  @import '../rattachements/Rattachements.less';

  .admin {
    position: relative;

    &-container {
      display: flex;
      flex-direction: column;
      gap: 32px;
    }

    &-wrapper {
      display: flex;
      gap: 18px;
      flex-wrap: wrap;
      justify-content: flex-end;

      .btn-ajout,
      .toggle-switch,
      .autocomplete {
        flex: 1;
      }
    }
  }

  @container (max-width: @breakpoint-mobile) {
    .admin {
      &-wrapper {
        flex-direction: column;
        gap: 12px;

        .autocomplete,
        .toggle-switch,
        .btn-ajout {
          max-width: none;
          width: auto;
        }
      }

      &-starter {
        display: grid;
        place-items: center;
        min-height: 100px;
        background-color: #f6f7f8;
      }
    }
  }
</style>
